import React, { useState, useRef, useEffect } from 'react';
import { ethers } from 'ethers';
import { useVirtualizer } from '@tanstack/react-virtual';
import Navigation from './Navigation';
import AboutPage from './AboutPage';
import FAQPage from './FAQPage';
import PixelListPage from './PixelListPage';
import StatusCounter from './StatusCounter';
import ETHPriceWidget from './ETHPriceWidget';
import AdminPage from './AdminPage';
import MintPixelsPage from './MintPixelsPage';
import FileUploadComponent from './FileUploadComponent';
import PixelDetailsModal from './PixelDetailsModal';
import GradientText from './GradientText';

// Enhanced metadata generation function - GENERIC TESTING VERSION
const generateEnhancedMetadata = (startPosition, size, imageUrl, websiteUrl, description) => {
  const GRID_SIZE = 100;
  const startPos = startPosition - 1;
  const startRow = Math.floor(startPos / GRID_SIZE);
  const startCol = startPos % GRID_SIZE;
  const endRow = startRow + size - 1;
  const endCol = startCol + size - 1;
  const endPosition = (endRow * GRID_SIZE) + endCol + 1;
  const pixelCount = size * size;
  const name = size === 1 
    ? `Test Minting Pixel #${startPosition}`
    : `Test Minting Pixels #${startPosition}-${endPosition}`;
  const pricePerPixel = 0.25;
  const totalPrice = pricePerPixel * pixelCount;
  const gridCoords = size === 1 
    ? `(${startRow + 1}, ${startCol + 1})`
    : `(${startRow + 1}, ${startCol + 1}) to (${endRow + 1}, ${endCol + 1})`;
  const detailedDescription = `${description || 'A piece of digital real estate on the new minting page.'}\n\n` +
    `This NFT represents ${pixelCount} pixel${pixelCount > 1 ? 's' : ''} on the new minting page - ` +
    `a blockchain reimagining of a minting website. ` +
    `Located at grid coordinates ${gridCoords}, this ${size}×${size} block ` +
    `is permanently stored on the blockchain.\n\n` +
    `🔗 Total Investment: ${totalPrice} ETH\n` +
    `📍 Position Range: #${startPosition}${size > 1 ? `-${endPosition}` : ''}\n` +
    `📏 Grid Size: ${size}×${size} (${pixelCount} pixels)\n` +
    `🌐 Website: ${websiteUrl || 'Not specified'}\n\n` +
    `Part of the new minting page project - where every pixel tells a story.`;
  const attributes = [
    {
      trait_type: "Grid Size",
      value: `${size}×${size}`
    },
    {
      trait_type: "Pixel Count",
      value: pixelCount,
      display_type: "number"
    },
    {
      trait_type: "Position Range",
      value: size === 1 ? `#${startPosition}` : `#${startPosition}-${endPosition}`
    },
    {
      trait_type: "Investment Amount (ETH)",
      value: totalPrice,
      display_type: "number"
    }
  ];
  if (websiteUrl) {
    attributes.push({
      trait_type: "Has Website",
      value: "Yes"
    });
    const cleanDomain = websiteUrl
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .toLowerCase();
    attributes.push({
      trait_type: "Website Domain",
      value: cleanDomain
    });
  } else {
    attributes.push({
      trait_type: "Has Website",
      value: "No"
    });
  }
  return {
    name: name,
    description: detailedDescription,
    image: imageUrl,
    external_url: websiteUrl || `localhost:3000`,
    animation_url: imageUrl,
    attributes: attributes,
    background_color: "FFFFFF",
    properties: {
      grid_size: size,
      pixel_count: pixelCount,
      start_position: startPosition,
      end_position: endPosition,
      coordinates: gridCoords,
      total_investment_eth: totalPrice,
      //website: websiteUrl
    }
  };
};

function App() {
  const [activePage, setActivePage] = useState('home');
  const [account, setAccount] = useState('');
  const [contract, setContract] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [selectedPixel, setSelectedPixel] = useState(null);
  const [mintedPixels, setMintedPixels] = useState({});
  const [selectedSize, setSelectedSize] = useState('1');
  const [isLoading, setIsLoading] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [isTransactionPending, setIsTransactionPending] = useState(false);
  const [approvedSubmissions, setApprovedSubmissions] = useState([]);
  const [pendingPixels, setPendingPixels] = useState({});
  const [selectedPixelDetails, setSelectedPixelDetails] = useState(null);


  // Grid constants
  const GRID_SIZE = 100; // Visual grid size
  const CELL_SIZE = 10;  // Each cell is 10×10 pixels
  const CONTRACT_GRID_SIZE = 100; // FIXED: Contract grid size now also 100

  // Create ref for the virtualized grid container
  const parentRef = useRef(null);
  const gridRef = useRef(null);

  // Window size state for responsive grid
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth - 50
  });

// Handle window resize
useEffect(() => {
	const handleResize = () => {
	  setWindowSize({
		width: window.innerWidth - 50
	  });
	};
  
	const handleKeyDown = (event) => {
	  if (event.key === 'Escape' && selectedPixelDetails) {
		setSelectedPixelDetails(null);
	  }
	};
  
	window.addEventListener('resize', handleResize);
	window.addEventListener('keydown', handleKeyDown);
	
	// Add the check for approved submissions when contract and account are available
	if (contract && account) {
	  checkApprovedSubmissions();
	}
  
	return () => {
	  window.removeEventListener('resize', handleResize);
	  window.removeEventListener('keydown', handleKeyDown);
	};
  }, [contract, account, selectedPixelDetails]);

// Scroll to grid when account connects
useEffect(() => {
  if (account && gridRef.current) {
    const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 100; // 40px Offset nach oben
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
}, [account]);

// Contract configuration
const contractAddress = "0xbAFb89D251aFFE81D9098207305Bd79Cb3f4AD16";

  // Contract ABI - Replace with your contract ABI
  const contractABI = [
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "to",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "approve",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			}
		],
		"name": "approvePosition",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [],
		"stateMutability": "nonpayable",
		"type": "constructor"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "sender",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			},
			{
				"internalType": "address",
				"name": "owner",
				"type": "address"
			}
		],
		"name": "ERC721IncorrectOwner",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "operator",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "ERC721InsufficientApproval",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "approver",
				"type": "address"
			}
		],
		"name": "ERC721InvalidApprover",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "operator",
				"type": "address"
			}
		],
		"name": "ERC721InvalidOperator",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "owner",
				"type": "address"
			}
		],
		"name": "ERC721InvalidOwner",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "receiver",
				"type": "address"
			}
		],
		"name": "ERC721InvalidReceiver",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "sender",
				"type": "address"
			}
		],
		"name": "ERC721InvalidSender",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "ERC721NonexistentToken",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "owner",
				"type": "address"
			}
		],
		"name": "OwnableInvalidOwner",
		"type": "error"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "account",
				"type": "address"
			}
		],
		"name": "OwnableUnauthorizedAccount",
		"type": "error"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "address",
				"name": "owner",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "approved",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "Approval",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "address",
				"name": "owner",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "operator",
				"type": "address"
			},
			{
				"indexed": false,
				"internalType": "bool",
				"name": "approved",
				"type": "bool"
			}
		],
		"name": "ApprovalForAll",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "_fromTokenId",
				"type": "uint256"
			},
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "_toTokenId",
				"type": "uint256"
			}
		],
		"name": "BatchMetadataUpdate",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "_tokenId",
				"type": "uint256"
			}
		],
		"name": "MetadataUpdate",
		"type": "event"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			},
			{
				"internalType": "uint256",
				"name": "size",
				"type": "uint256"
			},
			{
				"internalType": "string",
				"name": "tokenURI",
				"type": "string"
			}
		],
		"name": "mintPixelGroup",
		"outputs": [],
		"stateMutability": "payable",
		"type": "function"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			},
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "size",
				"type": "uint256"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "owner",
				"type": "address"
			}
		],
		"name": "NFTGroupMinted",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "owner",
				"type": "address"
			},
			{
				"indexed": false,
				"internalType": "string",
				"name": "tokenURI",
				"type": "string"
			}
		],
		"name": "NFTMinted",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "address",
				"name": "previousOwner",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "newOwner",
				"type": "address"
			}
		],
		"name": "OwnershipTransferred",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			}
		],
		"name": "PositionApproved",
		"type": "event"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			}
		],
		"name": "PositionRejected",
		"type": "event"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			}
		],
		"name": "rejectPosition",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "renounceOwnership",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "from",
				"type": "address"
			},
			{
				"internalType": "address",
				"name": "to",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "safeTransferFrom",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "from",
				"type": "address"
			},
			{
				"internalType": "address",
				"name": "to",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			},
			{
				"internalType": "bytes",
				"name": "data",
				"type": "bytes"
			}
		],
		"name": "safeTransferFrom",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "operator",
				"type": "address"
			},
			{
				"internalType": "bool",
				"name": "approved",
				"type": "bool"
			}
		],
		"name": "setApprovalForAll",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "address",
				"name": "submitter",
				"type": "address"
			},
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			},
			{
				"indexed": false,
				"internalType": "uint256",
				"name": "size",
				"type": "uint256"
			},
			{
				"indexed": false,
				"internalType": "string",
				"name": "tokenURI",
				"type": "string"
			}
		],
		"name": "SubmissionReceived",
		"type": "event"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			},
			{
				"internalType": "uint256",
				"name": "size",
				"type": "uint256"
			},
			{
				"internalType": "string",
				"name": "tokenURI",
				"type": "string"
			}
		],
		"name": "submitForApproval",
		"outputs": [],
		"stateMutability": "payable",
		"type": "function"
	},
	{
		"anonymous": false,
		"inputs": [
			{
				"indexed": true,
				"internalType": "address",
				"name": "from",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "address",
				"name": "to",
				"type": "address"
			},
			{
				"indexed": true,
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "Transfer",
		"type": "event"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "from",
				"type": "address"
			},
			{
				"internalType": "address",
				"name": "to",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "transferFrom",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "newOwner",
				"type": "address"
			}
		],
		"name": "transferOwnership",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "startPosition",
				"type": "uint256"
			},
			{
				"internalType": "string",
				"name": "newMetadata",
				"type": "string"
			}
		],
		"name": "updatePendingMetadata",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "withdraw",
		"outputs": [],
		"stateMutability": "nonpayable",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "approvedPositions",
		"outputs": [
			{
				"internalType": "bool",
				"name": "",
				"type": "bool"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "owner",
				"type": "address"
			}
		],
		"name": "balanceOf",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "firstOwner",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "getApproved",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "getTotalMintedPixels",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "GRID_10X10",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "GRID_1X1",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "GRID_2X2",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "GRID_5X5",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "address",
				"name": "owner",
				"type": "address"
			},
			{
				"internalType": "address",
				"name": "operator",
				"type": "address"
			}
		],
		"name": "isApprovedForAll",
		"outputs": [
			{
				"internalType": "bool",
				"name": "",
				"type": "bool"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "position",
				"type": "uint256"
			}
		],
		"name": "isPositionMinted",
		"outputs": [
			{
				"internalType": "bool",
				"name": "",
				"type": "bool"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "MAX_SUPPLY",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "name",
		"outputs": [
			{
				"internalType": "string",
				"name": "",
				"type": "string"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "nftPrice",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "owner",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "ownerOf",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "position",
				"type": "uint256"
			}
		],
		"name": "ownerOfPosition",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "pendingMetadata",
		"outputs": [
			{
				"internalType": "string",
				"name": "",
				"type": "string"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "pendingSize",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "pixelGroupSize",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "pixelGroupStart",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "pixelsSold",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "ROYALTY_PERCENTAGE",
		"outputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			},
			{
				"internalType": "uint256",
				"name": "salePrice",
				"type": "uint256"
			}
		],
		"name": "royaltyInfo",
		"outputs": [
			{
				"internalType": "address",
				"name": "receiver",
				"type": "address"
			},
			{
				"internalType": "uint256",
				"name": "royaltyAmount",
				"type": "uint256"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "",
				"type": "uint256"
			}
		],
		"name": "submitters",
		"outputs": [
			{
				"internalType": "address",
				"name": "",
				"type": "address"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "bytes4",
				"name": "interfaceId",
				"type": "bytes4"
			}
		],
		"name": "supportsInterface",
		"outputs": [
			{
				"internalType": "bool",
				"name": "",
				"type": "bool"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [],
		"name": "symbol",
		"outputs": [
			{
				"internalType": "string",
				"name": "",
				"type": "string"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "tokenName",
		"outputs": [
			{
				"internalType": "string",
				"name": "",
				"type": "string"
			}
		],
		"stateMutability": "view",
		"type": "function"
	},
	{
		"inputs": [
			{
				"internalType": "uint256",
				"name": "tokenId",
				"type": "uint256"
			}
		],
		"name": "tokenURI",
		"outputs": [
			{
				"internalType": "string",
				"name": "",
				"type": "string"
			}
		],
		"stateMutability": "view",
		"type": "function"
	}
];


// Helper function to check if an area is available
  const isAreaAvailable = async (startPosition, size) => {
	try {
	  // First check if selection is within bounds
	  if (!isSelectionWithinBounds(startPosition, size)) {
		return false;
	  }
	  // Check each position in the area
	  for (let r = 0; r < size; r++) {
		for (let c = 0; c < size; c++) {
		  // FIXED: Use GRID_SIZE
		  const position = startPosition + (r * GRID_SIZE) + c;
		  
		  // Check if already minted locally first
		  if (mintedPixels[position]) {
			return false;
		  }
		  
		  // Check if pending locally first
		  if (pendingPixels[position]) {
			return false;
		  }
		}
	  }
	  
	  // If we got here, the area is available based on local state
	  return true;
	} catch (err) {
	  console.error("Error checking area availability:", err);
	  return false;
	}
  };
  // Connect wallet function
  const connectWallet = async () => {
	try {
	  // First check if ethereum object exists (MetaMask or other wallet)
	  if (!window.ethereum) {
		setError('No web3 wallet detected. Please install MetaMask or another web3 wallet to continue.');
		return;
	  }
	  
	  // Request account access
	  const accounts = await window.ethereum.request({ 
		method: 'eth_requestAccounts' 
	  });
	  
	  // Check if any accounts were returned
	  if (!accounts || accounts.length === 0) {
		setError('No accounts found in your wallet. Please create an account and try again.');
		return;
	  }
	  
	  const account = accounts[0];
	  setAccount(account);
  
	  // Check account balance to detect empty wallet
	  const provider = new ethers.BrowserProvider(window.ethereum);
	  const balance = await provider.getBalance(account);
	  
	  if (balance <= 0) {
		setError('Your wallet has no ETH. You need some ETH to mint pixels and pay for gas fees.');
		// Still continue with connection so they can browse
	  }
	  
	  try {
		const signer = await provider.getSigner();
		const nftContract = new ethers.Contract(
		  contractAddress,
		  contractABI,
		  signer
		);
		
		// Test if contract is accessible on the current network
		try {
		  await nftContract.name();
		  setContract(nftContract);
		  
		  // Load data
		  await checkMintedPixels(nftContract);
		  await checkPendingPixels(nftContract);
		} catch (contractErr) {
		  console.error("Contract error:", contractErr);
		  setError('Contract not found on this network. Please switch to the correct network in your wallet.');
		  return;
		}
	  } catch (signerErr) {
		console.error("Signer error:", signerErr);
		setError('Error connecting to your wallet. Please check your wallet connection and try again.');
		return;
	  }
	} catch (err) {
	  console.error('Wallet connection error:', err);
	  // Provide user-friendly error messages based on common error types
	  if (err.code === 4001) {
		setError('You rejected the connection request. Please connect your wallet to continue.');
	  } else if (err.code === -32002) {
		setError('Wallet connection request pending. Please check your wallet.');
	  } else if (err.message && err.message.includes('already pending')) {
		setError('A connection request is already pending. Please check your wallet.');
	  } else {
		setError('Error connecting wallet: ' + (err.message || 'Unknown error. Please try again.'));
	  }
	}
  };

  const checkPendingPixels = async (nftContract = null) => {
  const contractToUse = nftContract || contract;
  if (!contractToUse) {
    console.log("No contract available, skipping checkPendingPixels");
    return;
  }
  try {
    console.log("Starting checkPendingPixels function");
    setIsLoading(true);
    const pendingPixels = {};
    // Get submission events to know which positions to check
    const submissionEvents = await contractToUse.queryFilter('SubmissionReceived');
    // For each submission, check if it's still pending in the contract
    for (const event of submissionEvents) {
      const startPosition = parseInt(event.args.startPosition.toString());
      try {
        // CHECK CONTRACT STATE DIRECTLY - this is the source of truth
        const submitter = await contractToUse.submitters(startPosition);
        // If position has a submitter (not zero address), it's pending
        if (submitter !== '0x0000000000000000000000000000000000000000') {
          const size = await contractToUse.pendingSize(startPosition);
          const sizeNum = parseInt(size.toString());
          console.log(`Position ${startPosition} is genuinely pending with size ${sizeNum}`);
          // Mark all positions in this group as pending
          for (let r = 0; r < sizeNum; r++) {
            for (let c = 0; c < sizeNum; c++) {
              const position = startPosition + (r * GRID_SIZE) + c;
              pendingPixels[position] = { 
                startPosition, 
                size: sizeNum,
                row: r,
                col: c 
              };
            }
          }
        }
      } catch (err) {
        // Position not pending, skip
        console.log(`Position ${startPosition} not pending:`, err.message);
      }
    }
    console.log("Final pending pixels count:", Object.keys(pendingPixels).length);
    setPendingPixels(pendingPixels);
    setIsLoading(false);
  } catch (err) {
    console.error('Error checking pending pixels:', err);
    setError('Error loading pending pixels: ' + err.message);
    setIsLoading(false);
  }
};

  // Check minted pixels
  const checkMintedPixels = async (nftContract) => {
	try {
	  setIsLoading(true);
	  const minted = {};
	  console.log("Starting to check minted pixels...");
  
	  // Get NFTGroupMinted events
	  const groupEvents = await nftContract.queryFilter('NFTGroupMinted');
	  console.log(`Found ${groupEvents.length} minted pixel groups`);
  
	  // Process in batches to avoid UI freezing
	  const batchSize = 10;
	  for (let i = 0; i < groupEvents.length; i += batchSize) {
		const batch = groupEvents.slice(i, i + batchSize);
		
		// Process each event in the batch
		await Promise.all(batch.map(async (event) => {
		  try {
			const startPosition = parseInt(event.args.startPosition.toString());
			const size = parseInt(event.args.size.toString());
			
			// Verify position is minted to avoid errors
			const isMinted = await nftContract.isPositionMinted(startPosition);
			if (!isMinted) return;
			// Get token URI and parse metadata
			try {
			  const uri = await nftContract.tokenURI(startPosition);
			  
			  if (uri && uri.startsWith('data:application/json;base64,')) {
				// Parse the base64-encoded JSON
				const json = atob(uri.split(',')[1]);
				const metadata = JSON.parse(json);
				
				// FIXED: Use GRID_SIZE
				for (let r = 0; r < size; r++) {
				  for (let c = 0; c < size; c++) {
					const position = startPosition + (r * GRID_SIZE) + c;
					
					minted[position] = {
					  image: metadata.image,
					  size,
					  startPosition,
					  row: r,
					  col: c,
					  metadata
					};
				  }
				}
			  }
			} catch (err) {
			  console.error(`Error getting token URI for position ${startPosition}:`, err);
			}
		  } catch (err) {
			console.error('Error processing NFTGroupMinted event:', err);
		  }
		}));
		
		// Update state occasionally to show progress
		if (i + batchSize >= groupEvents.length || i % 50 === 0) {
		  setMintedPixels({...minted});
		  // Allow UI to update
		  await new Promise(r => setTimeout(r, 1));
		}
	  }
  
	  console.log(`Loaded ${Object.keys(minted).length} minted pixels`);
	  setMintedPixels(minted);
	} catch (err) {
	  console.error('Error checking minted pixels:', err);
	  setError('Error loading minted pixels: ' + err.message);
	} finally {
	  setIsLoading(false);
	}
  };

  const checkApprovedSubmissions = async () => {
	if (!contract || !account) return;
	
	try {
	  // Get submission events for this user
	  const filter = contract.filters.SubmissionReceived(account);
	  const events = await contract.queryFilter(filter);
	  
	  const approved = [];
	  
	  for (const event of events) {
		const position = event.args.startPosition.toString();
		const isApproved = await contract.approvedPositions(position);
		
		if (isApproved) {
		  const size = event.args.size.toString();
		  approved.push({
			position,
			size
		  });
		}
	  }
	  
	  setApprovedSubmissions(approved);
	} catch (err) {
	  console.error("Error checking approved submissions:", err);
	}
  };

  // Handle image URL change
  const handleImageUrlChange = (e) => {
	setImageUrl(e.target.value);
	setError(''); // Nur Error clearen
  };
  
  // Show pixel details
  const showPixelDetails = async (position) => {
  try {
    if (!contract) return;
    
    const isMinted = await contract.isPositionMinted(position);
    if (!isMinted) {
      setSelectedPixel(position);
      setError('This pixel is not minted yet');
      return;
    }
    
    // Get the start position of this group
    const startPosition = await contract.pixelGroupStart(position);
    
    // Use START position for ALL contract calls
    const owner = await contract.ownerOf(startPosition);
    const size = await contract.pixelGroupSize(startPosition);
    const uri = await contract.tokenURI(startPosition);
    
    let metadata = {};
    if (uri && uri.startsWith('data:application/json;base64,')) {
      const json = atob(uri.split(',')[1]);
      metadata = JSON.parse(json);
    }
    
    setSelectedPixelDetails({
      tokenId: startPosition.toString(),  // ← .toString() hinzugefügt
      owner: owner,
      size: size.toString(),              // ← .toString() hinzugefügt  
      name: metadata.name || `Billion Dollar Site #${startPosition.toString()}`,
      image: metadata.image,
      website: metadata.website,
      description: metadata.description,
      contractAddress: contractAddress
    });
    
  } catch (err) {
    console.error("Error:", err);
    setError('Error loading pixel details: ' + err.message);
  }
};
  // Handle minting
  const handleSubmitForApproval = async () => {
  if (!contract || !selectedPixel || isTransactionPending) return;

  try {
    setIsTransactionPending(true);
    setError('Transaction pending... Please wait.');
    
    if (!imageUrl) {
      throw new Error('Image URL is required');
    }
    
    const row = Math.floor((selectedPixel - 1) / GRID_SIZE);
    const col = (selectedPixel - 1) % GRID_SIZE;
    console.log(`Submitting position: ${selectedPixel}, row: ${row}, col: ${col}, size: ${selectedSize}`);
    
    if (!isAreaAvailable(selectedPixel, parseInt(selectedSize))) {
      throw new Error('Selected area overlaps with already minted pixels');
    }
    
    const startPosition = selectedPixel;
    const size = parseInt(selectedSize);
    const tokenNameStr = `BDP #${startPosition}`;

    const metadata = {
      name: tokenNameStr,
      image: imageUrl,
      website: websiteUrl,
      description: description
    };

    const uri = `data:application/json;base64,${btoa(JSON.stringify(metadata))}`;
    const pixelCount = size * size;
    const pricePerPixel = 0.25;
    const totalPrice = ethers.parseEther((pricePerPixel * pixelCount).toFixed(18));

    // Call submitForApproval
    const tx = await contract.submitForApproval(startPosition, size, uri, {
      value: totalPrice
    });

    setError('Transaction submitted. Waiting for confirmation...');
    const receipt = await tx.wait();
    
    if (receipt.status === 1) {
      setError('✅ Successfully submitted for approval! Your pixels are now reserved. The page will refresh automatically in 5 seconds...');
      // FIX: Refresh pending pixels immediately after successful submission
      await checkPendingPixels();
      setTimeout(() => {
        setImageUrl('');
        setWebsiteUrl('');
        setDescription('');
        setSelectedPixel(null);
        setError('');
        setIsTransactionPending(false);
      }, 4000);
    } else {
      throw new Error('Transaction failed');
    }
  } catch (err) {
    console.error('Submission error:', err);
    setIsTransactionPending(false);
    
    if (err.code === 'ACTION_REJECTED') {
      setError('Transaction was rejected in MetaMask');
    } else if (
      err.code === 'INSUFFICIENT_FUNDS' || 
      (err.info?.error?.code === -32000) || 
      (err.info?.error?.message && err.info.error.message.toLowerCase().includes('insufficient funds')) ||
      (err.reason && err.reason.toLowerCase().includes('insufficient funds')) ||
      (err.message && err.message.toLowerCase().includes('insufficient funds'))
    ) {
      setError('❌ Insufficient ETH balance. You need more ETH to complete this transaction and pay for gas fees.');
    } else {
      setError('Error submitting for approval: ' + (err.reason || err.message || 'Unknown error'));
    }
  }
  };

  const handleMint = async (position, size) => {
	if (!contract || isTransactionPending) return;
	
	try {
	  setIsTransactionPending(true);
	  setError('Transaction pending... Please wait.');
	  
	  // No need to send value since payment was made during submission
	  const tx = await contract.mintPixelGroup(parseInt(position), size, "");
	  
	  setError('Transaction submitted. Waiting for confirmation...');
	  const receipt = await tx.wait();
	  
	  if (receipt.status === 1) {
		setError('Successfully minted your NFT!');
		
		// Refresh data
		setTimeout(async () => {
		  await checkMintedPixels(contract);
		  await checkPendingPixels();
		  // await checkApprovedSubmissions();
		  setIsTransactionPending(false);
		}, 2000);
	  }
	} catch (err) {
	  console.error('Minting error:', err);
	  setIsTransactionPending(false);
	  
	  if (err.code === 'ACTION_REJECTED') {
		setError('Transaction was rejected in MetaMask');
	  } else {
		setError('Error minting: ' + (err.reason || err.message || 'Unknown error'));
	  }
	}
  };

  // Helper: Prüft, ob Auswahl im Grid bleibt
const isSelectionWithinBounds = (startPosition, size) => {
  if (!startPosition) return false;
  const pos = startPosition - 1; // convert to 0-based
  const startCol = pos % GRID_SIZE;
  const startRow = Math.floor(pos / GRID_SIZE);
  const endCol = startCol + size - 1;
  const endRow = startRow + size - 1;
  // Must be within 0-99 for both row and column
  return (endCol < GRID_SIZE && endRow < GRID_SIZE);
};
// Comprehensive blocking check for selection
const getSelectionBlockingReason = (startPosition, size) => {
  if (!startPosition) return null;

  // Check boundary validation first
  if (!isSelectionWithinBounds(startPosition, size)) {
    const pos = startPosition - 1;
    const startCol = pos % GRID_SIZE;
    const startRow = Math.floor(pos / GRID_SIZE);
    const endCol = startCol + size - 1;
    const endRow = startRow + size - 1;

    if (endCol >= GRID_SIZE && endRow >= GRID_SIZE) {
      return `Exceeds grid boundaries (Column ${endCol + 1} > ${GRID_SIZE}, Row ${endRow + 1} > ${GRID_SIZE})`;
    } else if (endCol >= GRID_SIZE) {
      return `Extends beyond right edge (Column ${endCol + 1} > ${GRID_SIZE})`;
    } else if (endRow >= GRID_SIZE) {
      return `Extends beyond bottom edge (Row ${endRow + 1} > ${GRID_SIZE})`;
    } else {
      return 'Exceeds grid boundaries';
    }
  }

  // Check for minted or pending pixels in the selection area
  const pos = startPosition - 1;
  const startCol = pos % GRID_SIZE;
  const startRow = Math.floor(pos / GRID_SIZE);

  let hasMintedPixels = false;
  let hasPendingPixels = false;
  let mintedPositions = [];
  let pendingPositions = [];

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const position = startPosition + (r * GRID_SIZE) + c;
      if (mintedPixels[position]) {
        hasMintedPixels = true;
        mintedPositions.push(position);
      }
      if (pendingPixels[position]) {
        hasPendingPixels = true;
        pendingPositions.push(position);
      }
    }
  }

  // Return specific blocking reason
  if (hasMintedPixels && hasPendingPixels) {
    return `Overlaps with ${mintedPositions.length} minted and ${pendingPositions.length} pending pixels`;
  } else if (hasMintedPixels) {
    if (mintedPositions.length === 1) {
      return `Overlaps with minted pixel #${mintedPositions[0]}`;
    } else {
      return `Overlaps with ${mintedPositions.length} minted pixels`;
    }
  } else if (hasPendingPixels) {
    if (pendingPositions.length === 1) {
      return `Overlaps with pending pixel #${pendingPositions[0]} (awaiting approval)`;
    } else {
      return `Overlaps with ${pendingPositions.length} pending pixels (awaiting approval)`;
    }
  }

  return null; // No blocking issues
};
// Render virtualized grid
const renderGridWithCSS = () => {
  const size = parseInt(selectedSize);
  // Erstelle gridData mit KORREKTER Nummerierung (GRID_SIZE)
  const gridData = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      // FIXED: GRID_SIZE statt CONTRACT_GRID_SIZE
      const contractPosition = row * GRID_SIZE + col + 1;
      gridData.push({
        row,
        col,
        contractPosition,
        mintedData: mintedPixels[contractPosition],
        isPending: pendingPixels[contractPosition]
      });
    }
  }
  return (
    <div
      ref={gridRef}
      style={{
        width: '1000px',
        height: '1000px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_SIZE}, 10px)`,
        gridTemplateRows: `repeat(${GRID_SIZE}, 10px)`,
        backgroundColor: '#fff',
        border: '1px solid #ccc',
      }}
    >
      {gridData.map((cell) => {
        const isInSelectedArea = (() => {
          if (!selectedPixel) return false;
          const selPos = selectedPixel - 1;
          // FIXED: GRID_SIZE statt CONTRACT_GRID_SIZE
          const selStartCol = selPos % GRID_SIZE;
          const selStartRow = Math.floor(selPos / GRID_SIZE);
          const selEndCol = selStartCol + size - 1;
          const selEndRow = selStartRow + size - 1;
          return (cell.col >= selStartCol && cell.col <= selEndCol && 
                cell.row >= selStartRow && cell.row <= selEndRow);
        })();
        const isInHoverArea = (() => {
          if (!hoverPosition) return false;
          const hoverPos = hoverPosition - 1;
          // FIXED: GRID_SIZE statt CONTRACT_GRID_SIZE
          const hoverStartCol = hoverPos % GRID_SIZE;
          const hoverStartRow = Math.floor(hoverPos / GRID_SIZE);
          const hoverEndCol = hoverStartCol + size - 1;
          const hoverEndRow = hoverStartRow + size - 1;
          return (cell.col >= hoverStartCol && cell.col <= hoverEndCol && 
                cell.row >= hoverStartRow && cell.row <= hoverEndRow);
        })();
        const hoverValid = isInHoverArea && !cell.mintedData && 
          isSelectionWithinBounds(hoverPosition, size) &&
          isAreaAvailable(hoverPosition, size);
        const isHoverOutOfBounds = isInHoverArea && !isSelectionWithinBounds(hoverPosition, size);
        return (
          <div
            key={`${cell.row}-${cell.col}`}
            onClick={() => {
              if (cell.mintedData) {
                showPixelDetails(cell.mintedData.startPosition);
                return;
              }
              // Set the selected pixel first (so status can be calculated)
              setSelectedPixel(cell.contractPosition);
              // Now check if there are any blocking issues
              const blockingReason = getSelectionBlockingReason(cell.contractPosition, size);
              if (blockingReason) {
                // Don't clear the selection - keep it so user can see the status
                // The status display will show why it's blocked
                return;
              }
              // If we get here, selection is valid
              setError(''); // Clear any existing errors
            }}
            onMouseEnter={() => {
              setHoverPosition(cell.contractPosition);
            }}
            onMouseLeave={() => {
              if (hoverPosition === cell.contractPosition) {
                setHoverPosition(null);
              }
            }}
            style={{
              backgroundColor: isInSelectedArea ? 'rgba(0, 0, 255, 0.3)' :
                cell.isPending ? 'rgba(255, 165, 0, 0.3)' :
                isHoverOutOfBounds ? 'rgba(255, 0, 0, 0.4)' :
                hoverValid ? 'rgba(0, 255, 0, 0.2)' :
                isInHoverArea ? 'rgba(255, 0, 0, 0.2)' :
                '#fff',
              border: cell.mintedData ? 'none' : '1px solid #f0f0f0',
              cursor: cell.mintedData ? 'pointer' :
                cell.isPending ? 'not-allowed' :
                !isSelectionWithinBounds(cell.contractPosition, size) ? 'not-allowed' :
                !isAreaAvailable(cell.contractPosition, size) ? 'not-allowed' : 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {cell.mintedData && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden'
              }}>
                <img
                  src={cell.mintedData.image}
                  alt=""
                  style={{
                    position: 'absolute',
                    width: `${cell.mintedData.size * 10}px`,
                    height: `${cell.mintedData.size * 10}px`,
                    top: `-${cell.mintedData.row * 10}px`,
                    left: `-${cell.mintedData.col * 10}px`,
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.style.backgroundColor = '#f0f0f0';
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

  // Render the app
	return (
		<div style={{
			padding: '20px',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			maxWidth: '1200px',
			margin: '0 auto'
		}}>
			<h1 style={{ marginBottom: '20px' }}>
  <GradientText
    colors={["#40ffaa,rgb(2, 112, 13),rgb(29, 96, 66), #4079ff,rgb(6, 133, 76)"]}
    animationSpeed={8}
    showBorder={false}
  >
    Billion Dollar Site
  </GradientText>
</h1>
			<Navigation activePage={activePage} setActivePage={setActivePage} />

			<div style={{
				display: 'flex',
				width: '100%',
				justifyContent: 'space-between',
				alignItems: 'center',
				marginBottom: '15px'
			}}>
				<StatusCounter contract={contract} />
				<ETHPriceWidget />
			</div>

			<div style={{
				width: '100%',
				padding: '15px',
				backgroundColor: '#e3f2fd',
				borderRadius: '4px',
				marginBottom: '20px',
				border: '1px solid #bbdefb'
			}}>
				<p style={{ margin: 0 }}>
					<strong>Note:</strong> All submissions are subject to review before minting.
					Your pixels will be reserved during the approval process, and you'll only be charged if approved.
				</p>
			</div>

			{activePage === 'about' ? (
				<AboutPage />
				) : activePage === 'faq' ? (
				<FAQPage />
				) : activePage === 'pixellist' ? (
				<PixelListPage contract={contract} />
				) : activePage === 'mintpixels' ? (
				<MintPixelsPage />
				) : activePage === 'admin' ? (
				<AdminPage contract={contract} account={account} />
				) : (
				<>
					{!account ? (
  <>
    <button
      onClick={connectWallet}
      style={{
        padding: '10px 20px',
        marginBottom: '20px',
        backgroundColor: '#4CAF50',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      Connect Wallet
    </button>
    {/* Welcome Box */}
    <div style={{
      width: '100%',
      maxWidth: '800px',
      padding: '15px',
      backgroundColor: '#e3f2fd',
      borderRadius: '4px',
      marginBottom: '20px',
      border: '1px solid #bbdefb'
    }}>
      <p style={{ margin: '0 0 10px', fontWeight: 'bold', color: '#1976d2' }}>
        Welcome to the Billion Dollar Site! 🎉
      </p>
      <p style={{ margin: '0 0 10px', lineHeight: '1.5' }}>
        Own a piece of digital history by purchasing pixel blocks as NFTs on the Ethereum blockchain.
      </p>
      <p style={{ margin: 0, lineHeight: '1.5' }}>
        Everything you need to know about purchasing is explained in the <strong>"Mint Pixels"</strong> section. 
        All questions are answered in our <strong>"FAQ"</strong>, and learn about the project background in <strong>"About"</strong>.
      </p>
    </div>
  </>
) : (
						<>
							{/* Modern Contract + Connection Info */}
<div style={{
  width: '100%',
  display: 'flex',
  gap: '15px',
  marginBottom: '20px'
}}>
  {/* Contract Info - Links */}
  <div style={{
    flex: '2',
    padding: '15px',
    backgroundColor: '#fff3cd',
    borderRadius: '8px',
    border: '1px solid #ffeaa7'
  }}>
    <div style={{ 
      fontSize: '13px', 
      fontWeight: 'bold', 
      color: '#856404',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      🔒 OFFICIAL CONTRACT
    </div>
    <div style={{ 
      fontSize: '12px', 
      color: '#856404',
      fontFamily: 'monospace',
      marginBottom: '6px',
      wordBreak: 'break-all'
    }}>
      0xbAFb89D251aFFE81D9098207305Bd79Cb3f4AD16
    </div>
    <div style={{ 
      fontSize: '11px', 
      color: '#856404' 
    }}>
      Only interact with this verified contract. Beware of scams!
    </div>
  </div>

  {/* Connected Wallet - Rechts */}
  <div style={{
    flex: '1',
    padding: '15px',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    border: '1px solid #a5d6a7',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <div style={{ 
      fontSize: '12px', 
      fontWeight: 'bold', 
      color: '#2e7d32',
      marginBottom: '4px' 
    }}>
      ✅ CONNECTED
    </div>
    <div style={{ 
      fontSize: '13px', 
      color: '#2e7d32',
      fontFamily: 'monospace' 
    }}>
      {account.slice(0, 6)}...{account.slice(-4)}
    </div>
  </div>
</div>

							<div style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '20px',
								width: '100%',
								marginBottom: '20px'
							}}>
								{/* Left Column - Controls */}
								<div style={{
									flex: '1 1 300px',
									minWidth: '300px',
									padding: '15px',
									backgroundColor: '#f9f9f9',
									borderRadius: '4px',
									boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
								}}>
									<h3 style={{ marginTop: 0, marginBottom: '15px' }}>Purchase Options</h3>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px' }}>Selection Size:</label>
										<select
											value={selectedSize}
											onChange={(e) => setSelectedSize(e.target.value)}
											style={{
												width: '100%',
												padding: '8px',
												borderRadius: '4px',
												border: '1px solid #ddd'
											}}
										>
											<option value="1">1x1 (100x Pixels)</option>
											<option value="2">2x2 (400x Pixels)</option>
											<option value="5">5x5 (2500x Pixels)</option>
											<option value="10">10x10 (10000x Pixels)</option>
										</select>
									</div>

									<div style={{
										marginBottom: '15px',
										padding: '10px',
										backgroundColor: '#e8f5e9',
										borderRadius: '4px',
										border: '1px solid #a5d6a7',
										fontSize: '16px'
									}}>
										<div>Price: <strong>{(0.25 * parseInt(selectedSize) * parseInt(selectedSize))} ETH</strong></div>
										<div style={{ fontSize: '14px', color: '#555', marginTop: '5px' }}>
											{selectedSize}x{selectedSize} = {parseInt(selectedSize) * parseInt(selectedSize) * 100} Pixels = {parseInt(selectedSize) * parseInt(selectedSize) * 100}x0.0025 ETH
										</div>
									</div>
								</div>

								{/* Right Column - Pixel Content */}
								<div style={{
									flex: '1 1 300px',
									minWidth: '300px',
									padding: '15px',
									backgroundColor: '#f9f9f9',
									borderRadius: '4px',
									boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
								}}>
									<h3 style={{ marginTop: 0, marginBottom: '15px' }}>Set Pixel Content</h3>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px' }}>Image URL:</label>
										<input
											type="text"
											value={imageUrl}
											onChange={handleImageUrlChange}
											placeholder="https://example.com/image.jpg"
											style={{
												width: '100%',
												padding: '8px',
												borderRadius: '4px',
												border: '1px solid #ddd'
											}}
										/>
									</div>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px' }}>Website URL:</label>
										<input
											type="text"
											value={websiteUrl}
											onChange={(e) => setWebsiteUrl(e.target.value)}
											placeholder="https://yourwebsite.com"
											style={{
												width: '100%',
												padding: '8px',
												borderRadius: '4px',
												border: '1px solid #ddd'
											}}
										/>
									</div>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px' }}>
											Description: <span style={{ fontSize: '12px', color: '#666' }}>
												({description.length}/100 characters)
											</span>
										</label>
										<textarea
											value={description}
											onChange={(e) => {
												// Limit to 100 characters
												if (e.target.value.length <= 100) {
													setDescription(e.target.value);
												}
											}}
											placeholder="Describe your pixel or add a message"
											style={{
												width: '100%',
												padding: '8px',
												height: '80px',
												borderRadius: '4px',
												border: '1px solid #ddd',
												resize: 'vertical'
											}}
										/>
									</div>
								</div>
							</div>

							<div style={{ display: 'flex', marginBottom: '20px', gap: '10px' }}>
								<button
									onClick={handleSubmitForApproval}
									disabled={!selectedPixel || 
										!imageUrl.trim() || 
										!websiteUrl.trim() || 
										!description.trim() ||
									 isTransactionPending ||
									 getSelectionBlockingReason(selectedPixel, parseInt(selectedSize)) !== null}
									style={{
										opacity: (!selectedPixel || !imageUrl.trim() || !websiteUrl.trim() || !description.trim()) ? 0.5 : 1,
										cursor: (!selectedPixel || !imageUrl.trim() || !websiteUrl.trim() || !description.trim()) ? 'not-allowed' : 'pointer',
										padding: '12px 24px',
										backgroundColor: (() => {
											if (isTransactionPending) return '#ccc';
											if (!selectedPixel) return '#ccc';
											const blockingReason = getSelectionBlockingReason(selectedPixel, parseInt(selectedSize));
											if (blockingReason) return '#ff6b6b'; // Light red for grid errors
											if (error && error.toLowerCase().includes('insufficient')) return 'rgb(183 17 17)'; // Deep red for insufficient funds
											if (error && error.length > 0 && !(error.toLowerCase().includes('insufficient') || error.includes('Success') || error.includes('✅'))) return 'rgb(239 79 31)'; // Orange for generic errors
											return '#4CAF50'; // Green for ready to submit
										})(),
										color: 'white',
										border: 'none',
										borderRadius: '4px',
										fontSize: '16px',
										fontWeight: 'bold',
										boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
									}}
								>
									{(() => {
										if (isTransactionPending) {
											return 'Transaction Pending...';
										} else if (!selectedPixel) {
											return 'Select Pixel Position';
										} else {
											const blockingReason = getSelectionBlockingReason(selectedPixel, parseInt(selectedSize));
											if (blockingReason) {
												return '⚠️ Check Grid Error & Try Again';
											} else if (!imageUrl.trim() || !websiteUrl.trim() || !description.trim()) {
  return 'Fill All Fields Required';
											} else if (error && error.toLowerCase().includes('insufficient')) {
												return '❌ Insufficient Funds - Check Grid & Try Again';
											} else if (error && error.length > 0 && !(error.toLowerCase().includes('insufficient') || error.includes('Success') || error.includes('✅'))) {
												return '⚠️ Error - See Below';
											} else {
												return 'Submit for Approval';
											}
										}
									})()}

								</button>
								<div style={{ position: 'fixed', bottom: 0, right: 0, opacity: 0 }}>
									<button onClick={() => setActivePage('admin')}>Admin</button>
								</div>
							</div>

							<div style={{
							  display: 'flex',
							  flexDirection: 'column',
							  gap: '10px',
							  marginBottom: '20px',
							  padding: '15px',
							  backgroundColor: '#f5f5f5',
							  borderRadius: '8px',
							  border: '1px solid #ddd'
							}}>
							  <div style={{ display: 'flex', alignItems: 'center' }}>
								<span style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '120px' }}>Selected Pixel:</span>
								<span>{selectedPixel ? `#${selectedPixel}` : 'None'}</span>

							  </div>
							  <div style={{ display: 'flex', alignItems: 'center' }}>
								<span style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '120px' }}>Hover Position:</span>
								<span>
									{hoverPosition ? (
										<span>
											#{hoverPosition}
											{mintedPixels[hoverPosition] && <span style={{ color: '#ff9800', marginLeft: '5px' }}>🔒 MINTED</span>}
											{pendingPixels[hoverPosition] && <span style={{ color: '#9c27b0', marginLeft: '5px' }}>⏳ PENDING</span>}
											{!mintedPixels[hoverPosition] && ! pendingPixels[hoverPosition] && <span style={{ color: '#4CAF50', marginLeft: '5px' }}>✓ FREE</span>}
										</span>
									) : (
										'None'
									)}
								</span>
							  </div>
							  {selectedPixel && (
								<div>
								  <span style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
									Selection Status: 
									<span style={{ 
									  color: (() => {
										const blockingReason = getSelectionBlockingReason(selectedPixel, parseInt(selectedSize));
										return blockingReason ? '#f44336' : '#4CAF50';
									  })(),
									 

									  marginLeft: '8px'
									}}>
									  {(() => {
										const blockingReason = getSelectionBlockingReason(selectedPixel, parseInt(selectedSize));
										if (blockingReason) {
										  return `❌ ${blockingReason}`;
										} else {
										  return '✓ Valid selection - ready to submit';
										}
									  })()}
									</span>
								  </span>
								  <span style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>All Selected Pixels:</span>
								  <div style={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: '5px',
									maxWidth: '600px',
									marginTop: '5px'
								  }}>
									{(() => {
									  const pixels = [];
									  const size = parseInt(selectedSize);
									  const selPos = selectedPixel - 1; // convert to 0-based
									  // FIXED: GRID_SIZE statt CONTRACT_GRID_SIZE
									  const selStartCol = selPos % GRID_SIZE;
									  const selStartRow = Math.floor(selPos / GRID_SIZE);
									  for (let r = 0; r < size; r++) {
																			for (let c = 0; c < size; c++) {
										  // FIXED: GRID_SIZE für Berechnung
										  const position = (selStartRow + r) * GRID_SIZE + (selStartCol + c) + 1;
										  const isOutOfBounds = (selStartCol + c >= GRID_SIZE) || (selStartRow + r >= GRID_SIZE);
										  const isMinted = mintedPixels[position];
										  const isPending = pendingPixels[position];
										  // Determine pixel status and styling
										  let backgroundColor, borderColor, textColor, status;
																				  if (isOutOfBounds) {
											backgroundColor = 'rgba(255, 0, 0, 0.1)';
											borderColor = 'rgba(255, 0, 0, 0.2)';
											textColor = '#f44336';
											status = 'OUT';
										  } else if (isMinted) {
											backgroundColor = 'rgba(255, 152, 0, 0.1)';
											borderColor = 'rgba(255, 152, 0, 0.2)';
											textColor = '#ff9800';
											status = 'MINTED';
										  } else if (isPending) {
											backgroundColor = 'rgba(156, 39, 176, 0.1)';
											borderColor = 'rgba(156, 39, 176, 0.2)';
											textColor = '#9c27b0';
											status = 'PENDING';
										  } else {
											backgroundColor = 'rgba(0, 0, 255, 0.1)';
											borderColor = 'rgba(0, 0, 255, 0.2)';
											textColor = '#333';
											status = 'FREE';
										  }
										  pixels.push(
											<div key={position} style={{
											  padding: '3px 8px',
											  backgroundColor,
											  border: `1px solid ${borderColor}`,
											  borderRadius: '4px',
											  fontSize: '12px',
											  color: textColor,
											  display: 'flex',
											  flexDirection: 'column',
											  alignItems: 'center',
											  minWidth: '45px'
											}}>
											  <div>#{position}</div>
											  {status !== 'FREE' && (
												<div style={{ fontSize: '10px', fontWeight: 'bold' }}>
												  {status === 'OUT' && '❌'}
												  {status === 'MINTED' && '🔒'}
												  {status === 'PENDING' && '⏳'}
												</div>
											  )}
											</div>
										  );
										}
									  }
									  return pixels;
									})()}
								  </div>
								</div>
							  )}
							</div>

							{isLoading && (
								<div style={{
									marginBottom: '20px',
									padding: '10px',
									backgroundColor: '#e3f2fd',
									borderRadius: '4px',
									border: '1px solid #bbdefb',
									width: '100%',
									textAlign: 'center'
								}}>
									<div>Loading minted pixels...</div>
								</div>
							)}

							{error && (
							  <div style={{
								padding: '12px',
								marginBottom: '15px',
								borderRadius: '4px',
								backgroundColor: error.includes('Success') || error.includes('✅') ? '#E8F5E9' : '#FFEBEE',
								color: error.includes('Success') || error.includes('✅') ? '#2E7D32' : '#C62828',
								border: error.includes('Success') || error.includes('✅') ? '1px solid #A5D6A7' : '1px solid #EF9A9A',
								width: '100%',
								textAlign: 'center',
								fontWeight: '500'
							  }}>
								{error}
							  </div>
							)}

							{account && approvedSubmissions.length > 0 && (
								<div style={{
									width: '100%',
									padding: '15px',
									backgroundColor: '#e8f5e9',
									borderRadius: '4px',
									marginBottom: '20px',
									border: '1px solid #a5d6a7'
								}}>
									<h3 style={{ marginTop: 0 }}>Your Approved Submissions</h3>
									<p>The following submissions have been approved and are ready to mint:</p>

									{approvedSubmissions.map((submission, index) => (
										<div key={index} style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											padding: '10px',
											borderBottom: index < approvedSubmissions.length - 1 ? '1px solid #a5d6a7' : 'none'
										}}>
											<div>
												<p style={{ margin: '0 0 5px' }}><strong>Position:</strong> #{submission.position}</p>
												<p style={{ margin: 0 }}><strong>Size:</strong> {submission.size}×{submission.size}</p>
											</div>
											<button
												onClick={() => handleMint(submission.position, parseInt(submission.size))}
												style={{
													padding: '8px 16px',
													backgroundColor: '#4CAF50',
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													cursor: 'pointer'
												}}
											>
												Complete Minting
											</button>
										</div>
									))}
								</div>
							)}

							{/* Render the virtualized grid */}
							{renderGridWithCSS()}
						</>
					)}
				</>
				)}
				{/* Pixel Details Modal */}
				{selectedPixelDetails && (
					<PixelDetailsModal 
						pixelData={selectedPixelDetails} 
						onClose={() => setSelectedPixelDetails(null)} 
					/>
				)}
		</div>
	);
}
  
  export default App;